import React, {useMemo, useState} from 'react';
import './coursesWeeklyView.scss';
import dayjs from 'dayjs';
import {t} from 'i18next';
import {useSelector} from 'react-redux';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {solid, light} from '@fortawesome/fontawesome-svg-core/import.macro';
import {Colors} from '@arboxappv4/shared/src/styles/Colors';
import {getDisabledPagesCourseProps} from '@arboxappv4/shared/src/helpers/DisablePages';
import {
    buildCoursesByWeekday,
    getAttendanceDisplay,
    getWeekDayOrder
} from '../../../helpers/functions';
import {FlexColumnSection, FlexRowSection, SmallHeader} from '../../../styles/globalStyles';
import useScreenSize from '../../../helpers/hooks/useScreenSize';
import {isRTL} from '../../../helpers/i18n';
import EmptyCalendarView from '../../../svg/EmptyCalendarView';
import groupBy from 'lodash-es/groupBy';

const CoursesWeeklyView = (props) => {
    const {coursesFlat, isEmpty, onCourseClick, selectedLocation} = props;
    const globalLocation = useSelector(state => state.site.selectedLocation);
    const box = useSelector(state => state.site.box);
    const {isMobile} = useScreenSize();
    const [mobileSelectedDay, setMobileSelectedDay] = useState(() => dayjs().day());

    const colorView = !!box?.boxes_settings?.find(s => s.prop_name === 'siteColorCodedCourses')?.properties?.active;
    const weekDayOrder = getWeekDayOrder()
    const coursesByWeekday = useMemo(() => buildCoursesByWeekday(coursesFlat), [coursesFlat]);

    const navigateDay = (forward) => {
        const currentIndex = weekDayOrder.indexOf(mobileSelectedDay);
        const nextIndex = forward ? (currentIndex + 1) % weekDayOrder.length : (currentIndex - 1 + weekDayOrder.length) % weekDayOrder.length;
        setMobileSelectedDay(weekDayOrder[nextIndex]);
    };

    const getBookedBadge = () => {
        const badgeColor = colorView ? Colors.siteBlack : Colors.siteBookedBadge;
        return (
            <span className={`badge ${!isRTL() && 'right'}`}>
                <FontAwesomeIcon icon={solid('square-check')} color={badgeColor}/>
            </span>
        );
    };

    const renderCourseCard = ({course, event}) => {
        const displayProps = getDisabledPagesCourseProps(course);
        const attendanceDisplay = getAttendanceDisplay(displayProps, course);
        const cardStyle = colorView && course.box_categories?.category_color ? {backgroundColor: course.box_categories.category_color} : {};
        return (
            <div
                className={'course-card'}
                key={`course-card-${course.id}-${event.id}`}
                style={cardStyle}
                onClick={() => onCourseClick(course)}
            >
                <span className={'normal-text'}>
                    {dayjs(event.start, 'HH:mm').format(globalLocation.time_format)} - {dayjs(event.end, 'HH:mm').format(globalLocation.time_format)}
                </span>
                <span className={'bold-text'}>{course.series_name}</span>
                {displayProps.showCoach && course.coach && <span className={'normal-text'}>{course.coach.full_name}</span>}
                {displayProps.showCoach && course.second_coach && <span className={'normal-text'}>{course.second_coach.full_name}</span>}
                {(displayProps.showNumRegistered || displayProps.showNumOpenSpots) &&
                    <FlexRowSection gap={'8px'}>
                        <FontAwesomeIcon icon={solid('user')} color={Colors.siteBlack} size={'xs'}/>
                        <span className={'normal-text'}>{attendanceDisplay}</span>
                    </FlexRowSection>
                }
                {selectedLocation === 'all' && <span className={'normal-text gray-text'}>{course.locations_box.location}</span>}
                {course.isBookedUser && getBookedBadge()}
            </div>
        );
    };

    const renderDayEvents = (day) => {
        const dayEntries = coursesByWeekday[day] ?? [];
        const noEventsToday = !isEmpty && dayEntries.length === 0 &&
            <span className={'no-events-today'}>{t('no-events-today')}</span>;

        if (isMobile) {
            const groupedByType = groupBy(dayEntries, ({course}) => course.box_categories.name);
            return (
                <FlexColumnSection gap={'20px'}>
                    {Object.keys(groupedByType).map(coursesType => (
                        <FlexColumnSection gap={'5px'} overflow={'unset'} flex={'unset'} key={`mobile-type-${coursesType}`}>
                            <SmallHeader fontSize={'18px'}>{coursesType}</SmallHeader>
                            <FlexColumnSection gap={'10px'} overflow={'unset'} flex={'unset'}>
                                {groupedByType[coursesType].map(renderCourseCard)}
                            </FlexColumnSection>
                        </FlexColumnSection>
                    ))}
                    {noEventsToday}
                </FlexColumnSection>
            );
        }

        return (
            <div className={'day-events-wrapper'} key={`day-events-${day}`}>
                {dayEntries.map(renderCourseCard)}
                {noEventsToday}
            </div>
        );
    };

    if (isMobile) {
        return (
            <div className={'courses-weekly-view-wrapper mobile'}>
                <FlexRowSection justify={'space-between'} flex={'unset'}>
                    <div className={'day-nav-arrow'} onClick={() => navigateDay(false)}>
                        <FontAwesomeIcon icon={light('angle-left')} color={Colors.siteBlack} size={'lg'} flip={isRTL() ? 'horizontal' : 'vertical'}/>
                    </div>
                    <SmallHeader>{t(`day-${mobileSelectedDay}`)}</SmallHeader>
                    <div className={'day-nav-arrow'} onClick={() => navigateDay(true)}>
                        <FontAwesomeIcon icon={light('angle-right')} color={Colors.siteBlack} size={'lg'} flip={isRTL() ? 'horizontal' : 'vertical'}/>
                    </div>
                </FlexRowSection>
                <div className={'mobile-day-content'}>
                    {isEmpty
                        ? <div className={'empty-wrapper'}>
                            <EmptyCalendarView/>
                            <div className={'empty-text'}>{t('courses-empty-state')}</div>
                        </div>
                        : renderDayEvents(mobileSelectedDay)}
                </div>
            </div>
        );
    }

    return (
        <div className={'courses-weekly-view-wrapper'}>
            <div className={'weekly-grid-wrapper'}>
                {weekDayOrder.map(day => (
                    <div className={'day-header'} key={`day-header-${day}`}>
                        {t(`day-${day}`)}
                    </div>
                ))}
            </div>
            <div style={{overflow: 'auto'}}>
                <div className={`weekly-grid-wrapper ${isEmpty ? '' : 'courses-wrapper'}`}>
                    {weekDayOrder.map(renderDayEvents)}
                </div>
            </div>
            {isEmpty &&
                <div className={'empty-wrapper'}>
                    <EmptyCalendarView/>
                    <div className={'empty-text'}>{t('courses-empty-state')}</div>
                </div>}
        </div>
    );
};

export default CoursesWeeklyView;
